// Section 19: Elder's request. 4% per season, any season, when an elder lives here. An elder asks
// to stop working, to teach (skill 4+ and a school, Section 11), or to be allowed to leave to live
// with kin elsewhere (they leave alone at the next Spring or Summer). Choices: grant (happiness +5)
// or refuse (trust -5, happiness -5). Constants: config.events['elders-request'].
import type { Config } from '../../config';
import {
  adultsOf,
  choiceOutcome,
  departureTurn,
  eventEntry,
  eventPrompt,
  eventRng,
  promptString,
} from '../../core/content-helpers';
import { hasBuilding } from '../../core/queries';
import { defineEvent, noModifiers, type EventChoiceDef } from '../../core/registry';
import type { Delta, Person, ReadContext } from '../../core/types';

type Request = 'stop-working' | 'teach' | 'leave';

function requestOf(s: string): Request | null {
  return s === 'stop-working' || s === 'teach' || s === 'leave' ? s : null;
}

export function eldersRequest(config: Config) {
  const c = config.events['elders-request'];
  const canTeach = (ctx: ReadContext, p: Person): boolean => {
    const school = ctx.registry.jobs.teacher.requiresBuilding;
    return (
      p.job !== 'teacher' &&
      (school === null || hasBuilding(ctx.state, school)) &&
      Object.values(p.skills).some((level) => level >= c.teachMinSkill)
    );
  };
  const plan = (ctx: ReadContext): { elder: Person; request: Request } | null => {
    const elders = adultsOf(
      ctx,
      (p) => p.ageSeasons >= ctx.cfg.people.elderAtSeasons && p.plannedDeparture === null,
    );
    if (elders.length === 0) return null;
    const rng = eventRng(ctx, 'elders-request', 'plan');
    const elder = rng.pick(elders);
    const options: Request[] = [];
    if (elder.job !== 'rest') options.push('stop-working');
    if (canTeach(ctx, elder)) options.push('teach');
    options.push('leave');
    return { elder, request: rng.pick(options) };
  };
  const choices: EventChoiceDef[] = [
    {
      id: 'grant',
      label: 'Grant the request',
      preview: (_ctx, prompt) => [
        { key: `elder-grant-${requestOf(promptString(prompt, 'variant')) ?? 'leave'}`, params: {} },
      ],
      apply: (ctx, prompt) => {
        const elder = ctx.state.people[prompt.subjectIds[0] ?? 'p0'];
        const request = requestOf(promptString(prompt, 'variant'));
        if (elder === undefined || request === null) return [choiceOutcome(prompt, 'grant')];
        const out: Delta[] = [
          {
            kind: 'person',
            personId: elder.id,
            field: 'happiness',
            amount: c.grantHappiness,
            source: 'event:elders-request',
          },
        ];
        if (request === 'stop-working')
          out.push({
            kind: 'job',
            personId: elder.id,
            jobId: 'rest',
            source: 'event:elders-request',
          });
        if (request === 'teach')
          out.push({
            kind: 'job',
            personId: elder.id,
            jobId: 'teacher',
            source: 'event:elders-request',
          });
        if (request === 'leave')
          out.push({
            kind: 'planDeparture',
            personId: elder.id,
            reason: 'kin',
            atTurn: departureTurn(ctx, ctx.season.turn),
          });
        out.push(choiceOutcome(prompt, 'grant', { variant: request, name: elder.name }));
        return out;
      },
    },
    {
      id: 'refuse',
      label: 'Refuse',
      preview: () => [
        { key: 'elder-refuse', params: { trust: c.refuseTrust, happiness: c.refuseHappiness } },
      ],
      apply: (ctx, prompt) => {
        const elder = ctx.state.people[prompt.subjectIds[0] ?? 'p0'];
        if (elder === undefined) return [choiceOutcome(prompt, 'refuse')];
        return [
          {
            kind: 'person',
            personId: elder.id,
            field: 'trust',
            amount: c.refuseTrust,
            source: 'event:elders-request',
          },
          {
            kind: 'person',
            personId: elder.id,
            field: 'happiness',
            amount: c.refuseHappiness,
            source: 'event:elders-request',
          },
          choiceOutcome(prompt, 'refuse', { name: elder.name }),
        ];
      },
    },
  ];
  return defineEvent({
    id: 'elders-request',
    label: "Elder's request",
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring', 'summer', 'autumn', 'winter'],
    foreshadow: null,
    ledgerTag: null,
    weight: (ctx) => (plan(ctx) === null ? 0 : c.baseChance),
    text: (ctx) => {
      const p = plan(ctx);
      return eventEntry('elders-request', {
        variant: p?.request ?? 'leave',
        name: p?.elder.name ?? '',
      });
    },
    apply: (ctx) => {
      const p = plan(ctx);
      if (p === null) return [];
      return [
        eventPrompt(ctx, 'elders-request', choices, {
          params: { variant: p.request, name: p.elder.name },
          subjectIds: [p.elder.id],
          defaultChoiceId: 'grant',
        }),
      ];
    },
    choices,
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
